import * as signalR from '@microsoft/signalr'
import {
	EventMessageBase,
	Logger,
	eventHubService,
} from '@monthio/react-components'
import { openReplayService } from '../openReplayService'

export type SignalRImplementationConfiguration = {
	accessToken: string
	hubUrl: string
}

export class SignalRMessaging {
	private connection?: signalR.HubConnection
	private hubUrl = 'Unknown'
	private logger = new Logger('SignalRMessaging')
	private wsHook:
		| undefined
		| ((msgType: string, data: string, dir: 'up' | 'down') => void)

	public initialize = async (
		initConfig: SignalRImplementationConfiguration,
	) => {
		if (this.connection != null) {
			return
		}

		this.connection = this.initConnection(initConfig)
		this.hubUrl = initConfig.hubUrl
		await this.connection.start()
		this.wsHook = openReplayService.getWebsocketTrackingHook('Flow:SignalR')
		this.addStateSubscriptions()
		this.subscribe()
	}

	private subscribe = () => {
		this.connection!.on('All', (payload: EventMessageBase) => {
			this.logger.debug('signalR received a payload for topic All', payload)
			this.wsHook?.('All', JSON.stringify(payload), 'down')
			eventHubService.onEvent({
				...payload,
				source: this.hubUrl,
			})
		})
	}

	private initConnection = (
		initConfig: SignalRImplementationConfiguration,
	): signalR.HubConnection => {
		return new signalR.HubConnectionBuilder()
			.withUrl(initConfig.hubUrl, {
				accessTokenFactory: async () => {
					return await initConfig.accessToken
				},
			})
			.configureLogging(signalR.LogLevel.Information)
			.withAutomaticReconnect([0, 2000, 5000, 10000, 15000, 20000])
			.build()
	}

	private addStateSubscriptions = () => {
		this.connection?.onreconnecting((err) => {
			this.logger.warning(`Attempting to reconnect to ${this.hubUrl}`, err)
			this.wsHook?.('reconnecting', JSON.stringify(err), 'down')
		})

		this.connection?.onreconnected((connectionId) => {
			this.logger.warning(
				`Connection to ${this.hubUrl} re-established (reconnected)`,
				connectionId,
			)
			this.wsHook?.('reconnected', String(connectionId), 'down')
		})

		this.connection?.onclose((error) => {
			this.logger.warning(
				`Connection to ${this.hubUrl} closed (disconnected)`,
				error,
			)
			this.wsHook?.('closed', JSON.stringify(error), 'down')
		})
	}
}

export const signalRMessaging = new SignalRMessaging()
